import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting';
import { SelectPicker, Modal, Button } from "rsuite";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faChartColumn } from '@fortawesome/free-solid-svg-icons';
import SectionTitle from "./SectionTitle";
import MultiLingualContent from "../config/multilingualContent";
import { LanguageContext } from "../config/multilingualContext";
import { listDashInd, readDashboard, getCountries, readDashboardCompare } from "../functions";
import { formatNumber } from "./FormattedNumber";
import Loading from "./Loading";
import "rsuite/dist/rsuite.min.css";
import "../assets/css/dashcontent.css";

// Initialisation Highcharts
if (typeof Highcharts === 'object') {
  try {
    Exporting(Highcharts);
    OfflineExporting(Highcharts);
  } catch (e) {}
}

// Map des langues → locales ISO pour localeCompare
const LOCALE_MAP = {
  french:     'fr',
  english:    'en',
  portuguais: 'pt',
  swahili:    'sw',
};

// Fonction de localisation générique (accepte field)
const getLocalized = (obj, lang, field = 'label') => {
  if (!obj) return '';
  const fields = {
    french: obj[`${field}_fr`],
    english: obj[`${field}_en`],
    portuguais: obj[`${field}_pr`],
    swahili: obj[`${field}_sw`]
  };
  return fields[lang] || fields.english || fields.french || '';
};

// Options de comparaison (inchangé mais utilise getLocalized)
const getCompareHighchartsOptions = (compareData, language = 'french') => {
  if (!compareData?.axys?.length || !compareData?.chart?.length) return {};

  const categories = compareData.axys.map(pays => getLocalized(pays, language));
  const countryCodes = compareData.axys.map(pays => pays.code);
  const chartItem = compareData.chart[0];
  const firstCountry = countryCodes[0];
  const productIds = Object.keys(chartItem.serie?.[firstCountry] || {}).sort((a, b) => Number(a) - Number(b));

  const series = productIds.map(productId => {
    const productNameObj = chartItem.name?.[productId] || {};
    const productName = getLocalized(productNameObj, language) || `Produit ${productId}`;
    const color = chartItem.itemStyle?.normal?.color?.[productId];
    const data = countryCodes.map(code => chartItem.serie?.[code]?.[productId] ?? null);
    return {
      name: productName,
      data,
      color,
      type: chartItem.type === 'bar' ? 'column' : chartItem.type || 'column',
      stacking: 'normal'
    };
  });

  const indDetails = compareData.inddetails || {};
  const baseTitle = getLocalized(indDetails, language);
  const year = compareData.year ? ` - ${compareData.year}` : '';
  const title = baseTitle + year;

  const unitObj = compareData.unit || {};
  const unit = getLocalized(unitObj, language, 'unit_label');

  return {
    chart: { type: 'column', height: '50%',
      scrollablePlotArea: {
        minWidth: 800, // ou plus selon le nombre de pays
        scrollPositionX: 0
      }
     },
    title: { text: title },
    xAxis: {
      categories,
      title: { text: null },
      labels: { rotation: -60, style: { fontSize: '9px' } },
      step: 1
    },
    yAxis: { min: 0, title: { text: unit } },
    legend: { enabled: true, layout: 'horizontal', align: 'center', verticalAlign: 'bottom' },
    tooltip: {
      shared: true,
      valueDecimals: 2,
      formatter: function () {
        const countryName = this.points?.[0]?.key ?? this.x;
        const header = `<b>${countryName}</b><br/>`;
        const lines = this.points.map(p =>
          `<span style="color:${p.color}">●</span> ${p.series.name}: ${p.y} ${unit}`
        ).join('<br/>');
        return header + lines;
      }
    },
    plotOptions: {
      column: {
        borderRadius: 3,
        pointPadding: 0.06,
        groupPadding: 0.15,
        stacking: 'normal'
      }
    },
    series
  };
};

const DashContent = () => {
  const { language } = useContext(LanguageContext);
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState("");
  const [country, setCountry] = useState("");
  const [timeRange, setTimeRange] = useState('5y');
  const [chartType, setChartType] = useState('column');
  const [isStacked, setIsStacked] = useState(false);
  const [performData, setPerformData] = useState([]);
  const [countriesForPicker, setCountriesForPicker] = useState([]);
  const [rawAxys, setRawAxys] = useState([]);
  const [rawSerie, setRawSerie] = useState([]);
  const [title, setTitle] = useState("");
  const [unitLabel, setUnitLabel] = useState("");
  const [hcOptions, setHcOptions] = useState({});
  const chartRef = useRef(null);

  // États pour les données brutes (non traduites)
  const [rawCategories, setRawCategories] = useState([]);
  const [rawCountriesList, setRawCountriesList] = useState([]);

  // États pour la modale de comparaison
  const [showCompare, setShowCompare] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);

  // --- Chargement initial (données brutes) ---
  useEffect(() => {
    let isMounted = true;
    const init = async () => {
      setIsLoading(true);
      try {
        const [dashRes, countries] = await Promise.all([
          listDashInd(),
          getCountries()
        ]);
        const { dashCat } = dashRes.data;

        if (isMounted) {
          setRawCategories(dashCat || []);
          setRawCountriesList(countries || []);
        }
      } catch (err) {
        console.error("Erreur initialisation", err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };
    init();
    return () => { isMounted = false; };
  }, []);

  // --- Construction des listes des sélecteurs selon la langue ---
  useEffect(() => {
    if (!rawCategories.length && !rawCountriesList.length) return;

    const locale = LOCALE_MAP[language] ?? 'en';

    // Pays — triés A→Z selon la langue active
    const countryArray = rawCountriesList
      .map(item => ({
        label: getLocalized(item, language),
        value: item.id,
        group: getLocalized(item, language)
      }))
      .sort((a, b) =>
        (a.label || '').localeCompare(b.label || '', locale, { sensitivity: 'base' })
      );

    // Indicateurs — catégories triées A→Z, indicateurs dans chaque catégorie triés A→Z
    const perf = [];
    [...rawCategories]
      .sort((a, b) =>
        getLocalized(a, language).localeCompare(getLocalized(b, language), locale, { sensitivity: 'base' })
      )
      .forEach(cat => {
        [...(cat.indCatItemInd || [])]
          .sort((a, b) =>
            getLocalized(a, language).localeCompare(getLocalized(b, language), locale, { sensitivity: 'base' })
          )
          .forEach(ind => {
            perf.push({
              label: getLocalized(ind, language),
              value: ind.id,
              group: getLocalized(cat, language)
            });
          });
      });

    setPerformData(perf);
    setCountriesForPicker(countryArray);

    // Initialiser les sélections si elles sont vides
    if (perf.length && !dashboard) setDashboard(perf[0].value);
    if (countryArray.length && !country) setCountry(countryArray[0].value);
  }, [language, rawCategories, rawCountriesList]);

  // --- Chargement des données du graphique quand dashboard ou country change ---
  useEffect(() => {
    if (dashboard && country) {
      fetchDataForId(dashboard, country);
    }
  }, [dashboard, country]);

  // --- Rechargement quand la langue change (pour mettre à jour les labels du graphique) ---
  useEffect(() => {
    if (dashboard && country && rawAxys.length) {
      fetchDataForId(dashboard, country);
    }
  }, [language]);

  // --- Mise à jour du graphique quand les paramètres changent ---
  useEffect(() => {
    if (!rawAxys.length || !rawSerie.length) return;
    rebuildChartOptions();
  }, [timeRange, chartType, isStacked, rawAxys, rawSerie, title, unitLabel]);

  // --- Reconstruction des options Highcharts ---
  const rebuildChartOptions = useCallback(() => {
    const filterByRange = (axys, series, range) => {
      if (!axys.length || range === 'all') return { axys, series };
      const keep = { '1y': 1, '3y': 3, '5y': 5, '10y': 10 }[range] || axys.length;
      const start = Math.max(0, axys.length - keep);
      return {
        axys: axys.slice(start),
        series: series.map(s => ({ ...s, data: (s.data || []).slice(start) }))
      };
    };

    const { axys: filteredAxys, series: filteredSeries } = filterByRange(rawAxys, rawSerie, timeRange);

    const normalizeData = (data) => (data || []).map(pt => {
      if (typeof pt === 'object') {
        return { y: pt.y ?? pt.value ?? pt.v ?? 0, color: pt.color || pt.itemStyle?.color || pt.itemStyle?.normal?.color };
      }
      return pt;
    });

    const series = filteredSeries.map((s) => ({
      name: s.name,
      data: normalizeData(s.data),
      type: chartType,
      color: s.color || s.itemStyle?.color || s.itemStyle?.normal?.color,
      stack: isStacked && chartType === 'column' ? (s.stack || 'stack-1') : undefined,
      borderRadius: chartType === 'column' ? 3 : 0,
      dataLabels: { enabled: false }
    }));

    const options = {
      chart: { type: chartType, spacingTop: 16 },
      title: { text: title, style: { fontSize: '13px', color: '#000' } },
      xAxis: { categories: filteredAxys, crosshair: true },
      yAxis: { title: { text: unitLabel } },
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        floating: false,
        itemStyle: { fontSize: '11px' },
        itemMarginTop: 2,
        itemMarginBottom: 2,
        symbolWidth: 12,
        navigation: { enabled: true, animation: true, activeColor: '#111', inactiveColor: '#888', arrowSize: 12 },
        maxHeight: 90
      },
      tooltip: {
        shared: true,
        formatter: function() {
          const header = `<b>${this.category}</b><br/>`;
          const lines = this.points.map(p => {
            const val = formatNumber(p.y, language, 2);
            return `<span style="color:${p.color}">●</span> ${p.series.name}: ${val} ${unitLabel}`;
          }).join('<br/>');
          return header + lines;
        }
      },
      plotOptions: {
        series: { animation: { duration: 400 }, stacking: isStacked && chartType === 'column' ? 'normal' : undefined },
        column: { pointPadding: 0.06, borderWidth: 0, grouping: true },
        line: { marker: { enabled: true } },
        area: { marker: { enabled: true } }
      },
      exporting: { enabled: true },
      credits: { enabled: false },
      series
    };
    setHcOptions(options);
  }, [rawAxys, rawSerie, timeRange, chartType, isStacked, title, unitLabel, language]);

  // --- Chargement des données pour un indicateur et un pays ---
  const fetchDataForId = async (id, countryId) => {
    if (!id || !countryId) return;
    setLoading(true);
    try {
      const response = await readDashboard(id, countryId);
      const { chart, inddetails, axys, unit } = response.data;

      const series = chart.map(item => ({
        name: getLocalized(item.name, language),
        data: item.serie,
        itemStyle: item.itemStyle,
        type: item.type || 'bar',
        areaStyle: {}
      }));

      setRawAxys(axys || []);
      setRawSerie(series);
      setTitle(getLocalized(inddetails, language));
      setUnitLabel(getLocalized(unit, language, 'unit_label'));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (dashboard && country) {
      fetchDataForId(dashboard, country);
    }
  };

  const handleOpenCompare = async () => {
    if (!dashboard) return;
    setShowCompare(true);
    setCompareLoading(true);
    try {
      const response = await readDashboardCompare(dashboard);
      setCompareData(response.data);
    } catch (e) {
      setCompareData(null);
    } finally {
      setCompareLoading(false);
    }
  };

  if (isLoading) return <Loading />;

  return (
    <div className="container about-list-items mb-5">
      <SectionTitle pageTitle="tboard" />

      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-6 col-lg-5 col-xl-4 col-xs-12 mt-2">
            <SelectPicker
              label={<MultiLingualContent contentID="tboard" />}
              data={performData}
              groupBy="group"
              value={dashboard}
              onChange={setDashboard}
              block
              style={{ width: 'auto' }}
            />
          </div>
          <div className="col-md-6 col-lg-5 col-xl-4 col-xs-12 mt-2">
            <SelectPicker
              label={<MultiLingualContent contentID="country" />}
              data={countriesForPicker}
              value={country}
              onChange={setCountry}
              block
              style={{ minWidth: 100 }}
            />
          </div>
          {/*<div className="col-md-12 col-lg-2 col-xl-4 col-xs-12 mt-2 d-flex justify-content-start justify-content-md-end">
            <button className="btn btn-xs btn-primary w-100 w-md-auto" type="submit">
              <MultiLingualContent contentID="validation" />
            </button>
          </div>*/}
        </div>
      </form>

      <div className="row">
        <div className="about-list-items-right col-lg-12 col-xl-12 col-md-12 col-xs-12">
          <div className="card mt-3">
            <div className="card-body">
              {!loading && rawSerie.length > 0 ? (
                <>
                  <div className="chart-controls">
                    <select
                      className="chart-period"
                      value={timeRange}
                      onChange={(e) => setTimeRange(e.target.value)}
                    >
                      <option value="all"><MultiLingualContent contentID="allYears" /></option>
                      <option value="1y"><MultiLingualContent contentID="oneyear" /></option>
                      <option value="3y"><MultiLingualContent contentID="threeyears" /></option>
                      <option value="5y"><MultiLingualContent contentID="fiveyears" /></option>
                      <option value="10y"><MultiLingualContent contentID="tenyears" /></option>
                    </select>
                    <div className="chart-toggle-group">
                      <button
                        type="button"
                        className={`chart-toggle ${chartType === 'column' ? 'active' : ''}`}
                        onClick={() => setChartType('column')}
                        title="Barres"
                      >
                        <FontAwesomeIcon icon={faChartColumn} />
                      </button>
                      <button
                        type="button"
                        className={`chart-toggle ${chartType === 'line' ? 'active' : ''}`}
                        onClick={() => setChartType('line')}
                        title="Lignes"
                      >
                        <FontAwesomeIcon icon={faChartLine} />
                      </button>
                      <button
                        type="button"
                        className={`chart-toggle ${isStacked ? 'active' : ''}`}
                        onClick={() => setIsStacked(prev => !prev)}
                        title="Empiler"
                      >
                        <MultiLingualContent contentID="stack" />
                      </button>
                      <Button size="xs" onClick={handleOpenCompare} disabled={!dashboard}>
                        <MultiLingualContent contentID="compareView" />
                      </Button>
                    </div>
                  </div>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={hcOptions}
                    ref={chartRef}
                    containerProps={{ style: { width: '100%', height: '70vh' } }}
                  />
                </>
              ) : (
                !loading && <p><MultiLingualContent contentID="nodata" /></p>
              )}
              {loading && <Loading />}
            </div>
          </div>
        </div>
      </div>

      {/* Modale de comparaison */}
      <Modal open={showCompare} onClose={() => setShowCompare(false)} size="85rem" overflow={true}>
        <Modal.Header>
          <Modal.Title><MultiLingualContent contentID="compareView" /></Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: 'calc(100vh - 120px)', overflow: 'auto' }}>
          {compareLoading ? (
            <Loading />
          ) : compareData ? (
            <HighchartsReact
              highcharts={Highcharts}
              options={getCompareHighchartsOptions(compareData, language)}
              containerProps={{ style: { width: '100%', minHeight: '500px' } }}
            />
          ) : (
            <div><MultiLingualContent contentID="nodata" /></div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setShowCompare(false)} appearance="primary">
            <MultiLingualContent contentID="close" />
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DashContent;